import React, { useState, useMemo } from 'react';
import { AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const calcAge = (y, m, d) => {
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const mDiff = today.getMonth() + 1 - m;
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < d)) age -= 1;
  return age;
};

const AgeVerificationGate = ({ onVerify }) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [acceptedExplicit, setAcceptedExplicit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => now - i);
  }, []);
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  const age = calcAge(parseInt(year, 10), parseInt(month, 10), parseInt(day, 10));
  const isAdult = age !== null && age >= 18;
  const canSubmit = isAdult && acceptedExplicit && !submitting;

  const handleEnter = async () => {
    setError('');
    if (!day || !month || !year) {
      setError('Please enter your full date of birth.');
      return;
    }
    if (!isAdult) {
      setError('You must be 18 or older to enter this section.');
      return;
    }
    if (!acceptedExplicit) {
      setError('Please tick the box to confirm.');
      return;
    }

    setSubmitting(true);
    const dob = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    try {
      await fetch(`${API}/api/age-verification/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dob,
          confirmed_adult: true,
          confirmed_explicit: true,
        }),
      });
    } catch (e) {
      // Audit log failure shouldn't block the user, but record it client-side.
      console.error('Age verification audit log failed:', e);
    }

    // 30-day persistent grant
    const grant = {
      verified: true,
      method: 'self_declaration_v1',
      dob,
      grantedAt: Date.now(),
      expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
    };
    try {
      localStorage.setItem('outlaw_age_grant', JSON.stringify(grant));
    } catch (e) {
      console.warn('localStorage write blocked (private mode?), session-only grant:', e);
    }
    setSubmitting(false);
    onVerify(true);
  };

  return (
    <div
      className="min-h-screen bg-black flex items-center justify-center p-4"
      data-testid="age-verification-gate"
    >
      <div className="max-w-md w-full">
        <div className="bg-red-950/80 backdrop-blur-xl border-2 border-red-500/50 rounded-2xl p-6 md:p-8 text-center">
          <div className="flex justify-center mb-5">
            <div className="p-4 bg-red-600/20 rounded-full">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
          </div>

          <h1 className="text-2xl md:text-3xl font-black text-white mb-2">
            AGE VERIFICATION REQUIRED
          </h1>
          <p className="text-red-300 text-xs uppercase tracking-widest mb-5">
            18+ Restricted · Australian Age-Assurance Code
          </p>

          <div className="bg-black/60 rounded-lg p-5 mb-5 text-left">
            <p className="text-white font-bold mb-2 text-sm">⚠️ Explicit Content Warning</p>
            <p className="text-gray-300 text-xs leading-relaxed">
              This section contains graphic gangland footage that may be disturbing.
              Enter your date of birth to continue.
            </p>
          </div>

          {/* DOB picker */}
          <div className="mb-4">
            <label className="block text-left text-gray-400 text-xs uppercase tracking-widest mb-2">
              Date of Birth
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                data-testid="age-dob-day"
                className="bg-black/60 border border-red-900/40 text-white px-3 py-3 rounded text-sm focus:outline-none focus:border-red-500"
              >
                <option value="">Day</option>
                {days.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="age-dob-month"
                className="bg-black/60 border border-red-900/40 text-white px-3 py-3 rounded text-sm focus:outline-none focus:border-red-500"
              >
                <option value="">Month</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                data-testid="age-dob-year"
                className="bg-black/60 border border-red-900/40 text-white px-3 py-3 rounded text-sm focus:outline-none focus:border-red-500"
              >
                <option value="">Year</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Checkbox confirmation */}
          <label
            className="flex items-start gap-3 bg-black/40 border border-red-900/30 rounded-lg p-3 mb-4 cursor-pointer text-left hover:border-red-500/50 transition-colors"
            data-testid="age-confirm-label"
          >
            <input
              type="checkbox"
              checked={acceptedExplicit}
              onChange={(e) => setAcceptedExplicit(e.target.checked)}
              data-testid="age-confirm-checkbox"
              className="mt-0.5 h-4 w-4 accent-red-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-xs text-gray-300 leading-relaxed">
              I confirm I am <span className="text-white font-bold">18 years or older</span>,
              consent to viewing explicit content, and accept Outlaw Media's terms.
            </span>
          </label>

          {error && (
            <div
              className="bg-red-900/40 border border-red-500/50 text-red-300 text-xs px-3 py-2 rounded mb-3"
              data-testid="age-verify-error"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleEnter}
              disabled={!canSubmit}
              data-testid="age-verify-accept-btn"
              className={`w-full font-black py-3.5 px-6 rounded-lg transition-all duration-200 shadow-lg flex items-center justify-center gap-2 ${
                canSubmit
                  ? 'bg-red-600 hover:bg-red-700 text-white hover:shadow-red-500/50'
                  : 'bg-zinc-800 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}
              {submitting ? 'VERIFYING...' : 'ENTER 18+ SECTION'}
            </button>

            <button
              onClick={() => onVerify(false)}
              data-testid="age-verify-decline-btn"
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-gray-400 font-bold py-3 px-6 rounded-lg transition-colors text-sm"
            >
              Exit - I am under 18
            </button>
          </div>

          <p className="text-[10px] text-gray-600 mt-4 leading-snug">
            We do not store your date of birth in your browser or our servers as personal data.
            An anonymized audit log (hashed IP + timestamp) is retained for AU regulatory compliance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgeVerificationGate;
